/**
 * Pins products listed in ?top=handle-one,handle-two to the start of page 1.
 * Missing products are loaded by product handle through a dedicated alternate
 * product template, so products can be pinned across collections while the
 * theme's complete product-card markup (including quick add, prices, reviews
 * and swatches) is preserved.
 */

const TOP_PARAMETER = 'top';
const MAX_TOP_PRODUCTS = 12;
const FETCH_CONCURRENCY = 4;
const MIN_SKELETON_DURATION = 500;
const processingLists = new WeakSet();

/**
 * Accept a product handle or a full /products/handle URL.
 * @param {string} value
 * @returns {string}
 */
function normalizeHandle(value) {
  let handle = value.trim();

  try {
    if (/^https?:\/\//i.test(handle)) {
      const productUrl = new URL(handle);
      const match = productUrl.pathname.match(/\/products\/([^/]+)/i);
      handle = match?.[1] || '';
    }
  } catch {
    return '';
  }

  return handle
    .split(/[?#]/, 1)[0]
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
}

/**
 * @returns {string[]}
 */
function getRequestedHandles() {
  const value = new URL(window.location.href).searchParams.get(TOP_PARAMETER);
  if (!value) return [];

  return [...new Set(value.split(',').map(normalizeHandle).filter(Boolean))].slice(0, MAX_TOP_PRODUCTS);
}

/**
 * @param {Element} grid
 * @param {string} handle
 * @returns {HTMLElement | undefined}
 */
function findCard(grid, handle) {
  return /** @type {HTMLElement | undefined} */ (
    Array.from(grid.children).find((card) => card instanceof HTMLElement && card.dataset.productHandle === handle)
  );
}

/**
 * @param {HTMLElement} grid
 * @param {string[]} handles
 * @returns {Map<string, HTMLElement>}
 */
function createLoadingSkeletons(grid, handles) {
  /** @type {Map<string, HTMLElement>} */
  const skeletons = new Map();
  const fragment = document.createDocumentFragment();

  handles.forEach((handle, index) => {
    let skeleton = Array.from(grid.children).find(
      (child) => child instanceof HTMLElement && child.dataset.topProductSkeleton === handle
    );

    if (!(skeleton instanceof HTMLElement)) {
      skeleton = document.createElement('li');
      skeleton.className = `product-grid__item product-grid__item--${index} top-product-skeleton`;
      skeleton.dataset.topProductSkeleton = handle;
      skeleton.setAttribute('aria-hidden', 'true');
      skeleton.innerHTML = `
        <div class="top-product-skeleton__image top-product-skeleton__shape"></div>
        <div class="top-product-skeleton__details">
          <div class="top-product-skeleton__title top-product-skeleton__shape"></div>
          <div class="top-product-skeleton__price top-product-skeleton__shape"></div>
          <div class="top-product-skeleton__rating top-product-skeleton__shape"></div>
        </div>
      `;
      fragment.append(skeleton);
    }

    skeletons.set(handle, skeleton);
  });

  if (fragment.childElementCount > 0) grid.prepend(fragment);
  if (!grid.dataset.topSkeletonStartedAt) grid.dataset.topSkeletonStartedAt = String(Date.now());
  return skeletons;
}

/**
 * @param {string} handle
 * @param {HTMLElement} card
 * @param {Map<string, HTMLElement>} skeletons
 * @param {Map<string, HTMLElement>} foundCards
 */
function replaceSkeletonWithCard(handle, card, skeletons, foundCards) {
  card.dataset.page = '1';
  card.dataset.topProduct = 'true';

  const skeleton = skeletons.get(handle);
  if (skeleton?.isConnected) skeleton.replaceWith(card);

  foundCards.set(handle, card);
}

/**
 * Keep the loading feedback visible long enough to be perceived even when the
 * requested collection pages are already cached.
 * @param {HTMLElement} grid
 */
async function waitForMinimumSkeletonTime(grid) {
  const startedAt = Number(grid.dataset.topSkeletonStartedAt) || Date.now();
  const remaining = MIN_SKELETON_DURATION - (Date.now() - startedAt);

  if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
}

/**
 * The theme uses indexed item classes and 100%-height product-card elements.
 * Rebuild those indexes and force newly adopted custom elements to upgrade so
 * wide desktop grids include price/reviews in the row height calculation.
 * @param {HTMLElement} grid
 */
function refreshProductGrid(grid) {
  const cards = Array.from(grid.children).filter(
    (card) => card instanceof HTMLElement && card.classList.contains('product-grid__item')
  );

  cards.forEach((card, index) => {

    Array.from(card.classList).forEach((className) => {
      if (/^product-grid__item--\d+$/.test(className)) card.classList.remove(className);
    });
    card.classList.add(`product-grid__item--${index}`);

    if (window.customElements?.upgrade) window.customElements.upgrade(card);
  });

  grid.dataset.topProductsReady = 'true';

  requestAnimationFrame(() => {
    // Reading the final size flushes layout after cross-document nodes have
    // been adopted, then resize lets theme/app observers refresh their UI.
    grid.getBoundingClientRect();
    window.dispatchEvent(new Event('resize'));
  });
}

/**
 * Fetch one complete collection-style product card directly by product handle.
 * The alternate product template uses the same _product-card block settings as
 * templates/collection.json.
 * @param {string} handle
 * @returns {Promise<HTMLElement | null>}
 */
async function fetchProductCard(handle) {
  const storefrontRoot = window.Shopify?.routes?.root || '/';
  const url = new URL(`${storefrontRoot}products/${encodeURIComponent(handle)}`, window.location.origin);
  url.searchParams.set('view', 'ajax-card');
  url.hash = '';

  const response = await fetch(url.toString(), {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Unable to load product ${handle}: ${response.status}`);

  const documentFromPage = new DOMParser().parseFromString(await response.text(), 'text/html');
  return /** @type {HTMLElement | null} */ (
    Array.from(documentFromPage.querySelectorAll('[data-ajax-product-card-response]')).find(
      (card) => card instanceof HTMLElement && card.dataset.productHandle === handle
    ) || null
  );
}

/**
 * @param {HTMLElement} resultsList
 */
async function applyTopProducts(resultsList) {
  const handles = getRequestedHandles();
  if (handles.length === 0 || processingLists.has(resultsList)) return;

  const grid = resultsList.querySelector('[ref="grid"]');
  if (!(grid instanceof HTMLElement)) return;

  const currentUrl = new URL(window.location.href);
  const currentPage = Math.max(1, Number(currentUrl.searchParams.get('page')) || 1);
  const stateKey = `${currentPage}:${handles.join(',')}`;
  if (grid.dataset.topProductsState === stateKey) return;

  processingLists.add(resultsList);
  grid.dataset.topProductsState = stateKey;

  try {
    const requested = new Set(handles);

    // A pinned product is already shown on page 1, so remove its original card
    // from later pages when a visitor follows the collection pagination.
    if (currentPage > 1) {
      Array.from(grid.children).forEach((card) => {
        if (card instanceof HTMLElement && requested.has(card.dataset.productHandle || '')) card.remove();
      });
      refreshProductGrid(grid);
      return;
    }

    resultsList.setAttribute('aria-busy', 'true');
    const skeletons = createLoadingSkeletons(grid, handles);

    /** @type {Map<string, HTMLElement>} */
    const foundCards = new Map();
    handles.forEach((handle) => {
      const card = findCard(grid, handle);
      if (card) foundCards.set(handle, card);
    });

    if (foundCards.size < handles.length) {
      const missingHandles = handles.filter((handle) => !foundCards.has(handle));

      for (let index = 0; index < missingHandles.length; index += FETCH_CONCURRENCY) {
        const handleBatch = missingHandles.slice(index, index + FETCH_CONCURRENCY);
        const fetchedCards = await Promise.all(
          handleBatch.map((handle) =>
            fetchProductCard(handle).catch((error) => {
              console.warn('[Top products]', error);
              return null;
            })
          )
        );

        fetchedCards.forEach((card, cardIndex) => {
          if (card) foundCards.set(handleBatch[cardIndex], card);
        });
      }
    }

    await waitForMinimumSkeletonTime(grid);
    handles.forEach((handle) => {
      const card = foundCards.get(handle);
      if (card) replaceSkeletonWithCard(handle, card, skeletons, foundCards);
    });

    skeletons.forEach((skeleton) => skeleton.remove());
    delete grid.dataset.topSkeletonStartedAt;
    refreshProductGrid(grid);
  } finally {
    resultsList.removeAttribute('aria-busy');
    processingLists.delete(resultsList);

    // If filtering replaced the grid while pages were being fetched, apply the
    // same rule to the new grid after this request finishes.
    if (resultsList.querySelector('[ref="grid"]') !== grid) queueMicrotask(() => applyTopProducts(resultsList));
  }
}

function initializeTopProducts() {
  document.querySelectorAll('results-list').forEach((resultsList) => {
    if (resultsList instanceof HTMLElement) applyTopProducts(resultsList);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeTopProducts, { once: true });
} else {
  initializeTopProducts();
}

window.addEventListener('popstate', initializeTopProducts);

// Filters and the Theme Editor can replace the product grid without reloading
// this module. Reapply the URL rule whenever a new grid is inserted.
new MutationObserver(initializeTopProducts).observe(document.documentElement, {
  childList: true,
  subtree: true,
});
